import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import '../../styles/auth.css';

/**
 * Identity panel of the login, password pages and auth gates — slide 1 of the Anashopping presentation. Every access
 * page links the privacy notice (LGPD), except the notice itself (`privacyLink={false}`).
 */
export function AuthShell({ children, privacyLink = true }: { children: ReactNode; privacyLink?: boolean }) {
  return (
    <div className="auth-shell">
      <section className="auth-brand" aria-label="Anashopping — gestão do estacionamento">
        <img
          className="logo"
          src="/brand/anashopping-logo-branco.png"
          alt="Anashopping"
          width={280}
          height={39}
        />
        <div>
          <h1>Gestão do estacionamento</h1>
          <p className="lead">Controle financeiro e operacional</p>
          <div className="pills" aria-hidden="true">
            <span className="pill">Operação</span>
            <span className="pill">Conciliação</span>
            <span className="pill">Resultados</span>
          </div>
        </div>
        <p className="foot">Anashopping · Anápolis — GO</p>
      </section>
      <main className="auth-main">
        <div className="auth-card">
          {children}
          {privacyLink ? (
            <p className="help">
              <Link to="/privacidade">Aviso de privacidade</Link>
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/**
 * A button disabled while its request runs drops the focus to <body> when it was the focused element: after a
 * failure, put the focus back in the form so a keyboard user does not start over from the top of the page.
 * A fast answer can arrive before the browser has moved the focus off the disabled button, hence `:disabled`.
 */
export function restoreFocus(target: HTMLElement | null) {
  const active = document.activeElement;
  if (!active || active === document.body || active.matches(':disabled')) target?.focus();
}

/**
 * Error of an auth form. A new `attempt` remounts the alert, so a repeated error (same text) is announced again
 * and the shake animation plays once per failure.
 */
export function FormError({ error, attempt = 0 }: { error: string | null; attempt?: number }) {
  return error ? (
    <div key={attempt} className="alert danger auth-alert" role="alert">
      <CircleAlert size={18} aria-hidden="true" />
      <span>{error}</span>
    </div>
  ) : null;
}
